/**
 * ステージの図（`/stages/$stageId`）— 見出しと出口の帯、メインフロー 1 本の図、サブフローの札の帯。
 *
 * **1 図 ＝ 1 フロー。サブフローはメインフローの図に混ぜない。** メインフローを業務フローの図と
 * 同じ部品（`StoryFlowView` / `PjStoryFlowView`）で描き、サブフローは札（名前だけ）で並べて、
 * 押すとその業務フローの図へ飛ぶ。ステージ用に別の描画を作ると、同じメインフローが画面によって
 * 違う図になる。
 *
 * **鎖の中の順と前後のステージは読取が返す**（`getFlow` のステージの `chain`。共通部の 1 関数が
 * 入口の参照から導いたもの）。画面で入口を辿り直さない。
 *
 * **案件は検索条件（`?pj=`）で受ける。** 案件を選んでいれば、中身を見る画面の原則どおりメイン
 * フローを変更前 / 変更後の左右で出す（`?compare=0` で変更後 1 面。メインフローの図に差が無ければ
 * 1 面で、比較のボタンは押せない）。案件が新設・削除した
 * ステージは、その面を引かない（案件の変更の行から先に分かる。引けば必ず未存在で返る）。
 *
 * **入った直後に見えるのはステージ名とメインフローの図**（Dhik 2026-09-29「テキスト量が多いことが
 * 課題」）。鎖の中の順は名前を指すと出し、説明は名前の横の ⓘ に入れる。出口の帯と前のステージは
 * 「出口」を押して開く畳みに入れる（図の終わりの札でも読める）。何も選んでいないときの右パネルは
 * 置かない（中身が見出しと重なる）。サブフローは 1 本以上あるときだけ札の節を出す。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ステージ 1 件を、名前（鎖の中の順は指すと出る・説明は ⓘ）・畳んだ出口の帯（出口の業務 → 結末 → 次のステージ、前のステージ）・メインフローの図・サブフローの札として、マスターまたは案件の左右比較で描く。
 * @graph-connects useStage [calls] ステージの図の取得
 * @graph-connects usePjNodePresence [calls] 案件が新設・削除したステージかの判定
 * @graph-connects StoryFlowView [calls] メインフローの図の描画
 * @graph-connects PjStoryFlowView [calls] 案件から見たメインフローの左右比較の描画
 */
import type { FlowView, StageView, SupportFlowCardView } from '@cortex/domain-spec-console';
import { Skeleton } from '@cortex/web-design-system/components/ui/Skeleton';
import { Link, useParams, useSearch } from '@tanstack/react-router';
import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState, NotFoundState } from '../components/spec/fallback-states.js';
import { NodeTypeBadge } from '../components/spec/NodeTypeBadge.js';
import { usePjChanges, usePjRequirements } from '../hooks/queries/spec-graph-element-pj-queries.js';
import { useStage, type StageDiagram } from '../hooks/queries/spec-graph-flow-queries.js';
import { isNotFoundError } from '../lib/orpc-errors.js';
import { useCompareSearch } from './flow-workspace/use-compare-search.js';
import { usePjNodePresence } from './pj-master-presence.js';
import {
  buildStoryFlowPjDiff,
  buildStoryFlowReasons,
  hasStoryFlowPjDiff,
} from './pj-story-flow-diff.js';
import { BUSINESS_MAP_ROUTE, STAGE_ROUTE } from './route-paths.js';
import { FlowInfoMark } from './story-flow/flow-info-mark.js';
import { refLabelOf } from './story-flow/flow-text.js';
import { PjStoryFlowView } from './story-flow/PjStoryFlowView.js';
import { readFlowIdParam, readPjSearchParam } from './story-flow/story-flow-params.js';
import { StoryFlowView } from './story-flow/StoryFlowView.js';
import { BusinessDetailLink } from './view-links/business-logic.js';
import { FlowLink, StageLink } from './view-links/flow.js';

/**
 * ステージの図を、URL の誤り・マスター・案件の 3 通りに振り分ける。
 *
 * @returns ステージの図
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ステージの図を、URL の誤り・マスター・案件の左右比較に振り分けて描く。
 * @graph-connects readFlowIdParam [calls] URL からステージの id を読む
 * @graph-connects readPjSearchParam [calls] 検索条件から案件番号を読む
 */
export function StagePage() {
  const { stageId } = useParams({ from: STAGE_ROUTE });
  const { pj } = useSearch({ from: STAGE_ROUTE });
  const id = readFlowIdParam(stageId);
  const pjNumber = readPjSearchParam(pj);

  if (id === null) return <StageNotFound id={stageId} />;
  if (pjNumber === null) return <MasterStageBody stageId={id} rawId={stageId} />;
  return <PjStageBody stageId={id} pjNumber={pjNumber} rawId={stageId} />;
}

/** 本体の props。 @graph-connects none */
interface StageBodyProps {
  /** ステージの id */
  stageId: number;
  /** URL に書かれていた綴り */
  rawId: string;
}

/**
 * マスターのステージの図。
 *
 * @param props ステージの id と URL の綴り
 * @returns 取得中・未存在・失敗・本体のいずれか
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business マスターのステージを取得し、出口の帯・メインフローの図・サブフローの札で描く。
 * @graph-connects useStage [calls] ステージの図の取得
 * @graph-connects StoryFlowView [calls] メインフローの図の描画
 */
function MasterStageBody({ stageId, rawId }: StageBodyProps) {
  const { t } = useTranslation();
  const stage = useStage(stageId);
  const [selectedNodeId, setSelectedNodeId] = useState<number | null>(null);

  if (isNotFoundError(stage.error)) return <StageNotFound id={rawId} />;
  if (stage.error !== null && stage.error !== undefined) return <ErrorState error={stage.error} />;
  if (stage.data === undefined) return <StageSkeleton label={t('common.loading')} />;

  const { mainFlow } = stage.data;
  return (
    <StageLayout
      diagram={stage.data}
      pjNumber={undefined}
      main={
        mainFlow !== null && (
          <StoryFlowView
            flow={mainFlow}
            showHeading={false}
            selectedNodeId={selectedNodeId}
            onSelect={(nodeId) => {
              setSelectedNodeId((current) => (current === nodeId ? null : nodeId));
            }}
          />
        )
      }
    />
  );
}

/** 案件の本体の props。 @graph-connects none */
interface PjStageBodyProps extends StageBodyProps {
  /** 重ねる案件番号 */
  pjNumber: number;
}

/**
 * 片側が無いときに置く空のメインフロー。名前と型だけを相手から写す。
 * @graph-connects none
 */
function emptyFlowLike(source: FlowView): FlowView {
  return { ...source, lanes: [], nodes: [], transitions: [] };
}

/**
 * 案件から見たステージの図。メインフローを変更前 / 変更後で引いて左右に並べる。
 *
 * **片側の未存在は失敗ではない。** 案件が新設したステージには変更前が無く、削除したステージには
 * 変更後が無い。空の骨格を置けば「全部が追加 / 削除」がそのまま差分になる。
 *
 * **無いと分かっている面は引かない。** 案件が新設したかどうかは案件の変更の行に書いてある
 * （`usePjNodePresence`。業務詳細・案件から見た業務フローと同じ判定）。問い合わせてから未存在と
 * 知る形は、押していない利用者のコンソールと通信の記録に 404 を 1 本ずつ積む。
 *
 * @param props ステージの id・案件番号・URL の綴り
 * @returns 取得中・未存在・失敗・本体のいずれか
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件から見たステージを変更前・変更後で引き、メインフローを左右比較・サブフローを札で描く。
 * @graph-connects useStage [calls] 変更前と変更後のステージの図の取得
 * @graph-connects usePjNodePresence [calls] 案件が新設・削除したステージかの判定
 * @graph-connects buildStoryFlowPjDiff [calls] メインフローの変更前後の突き合わせ
 * @graph-connects hasStoryFlowPjDiff [calls] メインフローの図に変更前との差が有るかの判定
 * @graph-connects useCompareSearch [calls] 左右比較の開閉
 * @graph-connects PjStoryFlowView [calls] メインフローの左右比較の描画
 */
function PjStageBody({ stageId, pjNumber, rawId }: PjStageBodyProps) {
  const { t } = useTranslation();
  const presence = usePjNodePresence(pjNumber, stageId);
  const before = useStage(
    stageId,
    { number: pjNumber, side: 'before' },
    presence.master === 'present',
  );
  const after = useStage(
    stageId,
    { number: pjNumber, side: 'after' },
    presence.applied === 'present',
  );
  const changes = usePjChanges(pjNumber);
  const requirements = usePjRequirements(pjNumber);

  /* 無いことは 2 通りに分かる — 変更の行から先に分かる（引かない）か、引いて未存在で返るか。 */
  const beforeMissing = presence.master === 'absent' || isNotFoundError(before.error);
  const afterMissing = presence.applied === 'absent' || isNotFoundError(after.error);
  const changeRows = changes.data?.changes;
  const requirementRows = requirements.data?.requirements;
  const beforeMain = before.data?.mainFlow ?? null;
  const afterMain = after.data?.mainFlow ?? null;
  const diff = useMemo(() => {
    if (changeRows === undefined) return null;
    const source = afterMain ?? beforeMain;
    if (source === null) return null;
    const left = beforeMain ?? emptyFlowLike(source);
    const right = afterMain ?? emptyFlowLike(source);
    return { left, right, diff: buildStoryFlowPjDiff(left, right, changeRows) };
  }, [beforeMain, afterMain, changeRows]);
  const reasons = useMemo(() => buildStoryFlowReasons(requirementRows ?? []), [requirementRows]);
  /*
   * 左右に並べるのはメインフローの図なので、差もメインフローの図で見る。サブフローだけを変えた
   * 案件では、同じメインフローが 2 枚並ぶだけになる。
   */
  const compare = useCompareSearch(diff !== null && hasStoryFlowPjDiff(diff.diff));

  if (beforeMissing && afterMissing) return <StageNotFound id={rawId} />;
  const failure = [before, after].find(
    (query) => query.error !== null && query.error !== undefined && !isNotFoundError(query.error),
  )?.error;
  if (failure !== undefined) return <ErrorState error={failure} />;
  for (const query of [changes, requirements]) {
    if (query.error !== null && query.error !== undefined)
      return <ErrorState error={query.error} />;
  }
  const shown = after.data ?? before.data;
  if (
    shown === undefined ||
    (!beforeMissing && before.data === undefined) ||
    (!afterMissing && after.data === undefined) ||
    changeRows === undefined ||
    requirementRows === undefined
  ) {
    return <StageSkeleton label={t('common.loading')} />;
  }

  /* メインフローの見出しは、変更後に無ければ変更前のものを出す（案件がメインフローごと消した場合）。 */
  const diagram = { ...shown, mainFlow: afterMain ?? beforeMain };

  return (
    <StageLayout
      diagram={diagram}
      pjNumber={pjNumber}
      main={
        diff !== null && (
          <PjStoryFlowView
            pjNumber={pjNumber}
            before={diff.left}
            after={diff.right}
            afterMissing={afterMissing || afterMain === null}
            diff={diff.diff}
            reasons={reasons}
            split={compare.split}
            compareDisabled={compare.disabled}
            onToggleSplit={compare.toggle}
            showHeading={false}
          />
        )
      }
    />
  );
}

/** `StageLayout` の props。 @graph-connects none */
interface StageLayoutProps {
  /** ステージの図（見出し・サブフローに使う面） */
  diagram: StageDiagram;
  /** 重ねる案件番号。master なら undefined */
  pjNumber: number | undefined;
  /** メインフローの図。メインフローが無ければ false */
  main: ReactNode;
}

/**
 * ステージの図の骨組み — 見出しと出口の帯・メインフロー・サブフローの札。
 *
 * @param props ステージの図・案件番号・メインフローの図
 * @returns ステージの図
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ステージの見出しと畳んだ出口の帯・メインフローの図（名前を見出しに）・1 本以上あるときのサブフローの札を並べて描く。
 * @graph-connects StageHeading [calls] 見出しと出口の帯
 * @graph-connects SupportFlowCard [calls] サブフローの札
 * @graph-connects FlowLink [calls] 図の見出しにするメインフローの業務フローの図へのリンク
 */
function StageLayout({ diagram, pjNumber, main }: StageLayoutProps) {
  const { t } = useTranslation();
  const { stage, mainFlow, supportFlows } = diagram;

  return (
    <div className="flex min-h-0 flex-col gap-6 overflow-y-auto">
      <StageHeading stage={stage} pjNumber={pjNumber} />

      <section className="flex flex-col gap-2" data-stage-section="main">
        {/*
          節が 1 つなので「メインフロー」の見出しと札は出さない。図の見出しは業務フローの名前にし、
          業務フローの図へ行く唯一の入口にする。
        */}
        {mainFlow !== null && (
          <h2 className="text-base font-semibold" data-stage-main-flow={mainFlow.flowId}>
            <FlowLink flowId={mainFlow.flowId} pjNumber={pjNumber}>
              {mainFlow.label === '' ? t('storyFlow.labelMissing') : mainFlow.label}
            </FlowLink>
          </h2>
        )}
        {mainFlow === null || main === false ? (
          <p className="text-sm text-[var(--color-text-muted)]" data-stage-main="missing">
            {t('stage.mainFlowMissing')}
          </p>
        ) : (
          main
        )}
      </section>

      {/* サブフローが無ければ節ごと出さない。 */}
      {supportFlows.length > 0 && (
        <section className="flex flex-col gap-2" data-stage-section="support">
          <h2 className="text-base font-semibold text-[var(--color-text-primary)]">
            {t('stage.supportFlows', { count: supportFlows.length })}
          </h2>
          <ul className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {supportFlows.map((card) => (
              <li key={card.flowId}>
                <SupportFlowCard card={card} pjNumber={pjNumber} />
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

/** `StageHeading` の props。 @graph-connects none */
interface StageHeadingProps {
  /** ステージ */
  stage: StageView;
  /** 重ねる案件番号。master なら undefined */
  pjNumber: number | undefined;
}

/**
 * ステージの見出し（型の札・名前・説明の ⓘ）と、畳んだ出口の帯。
 *
 * **案件のときも見出しに「配下に変更」を出さない**（Dhik 2026-09-29「PJモードの業務マップでも配下に
 * 変更を出さず、色もつけないでほしい」の「でも」＝業務マップ以外も）。配下の業務フローを案件が変えた
 * ことは、メインフローの図の差分とサブフローの札の先で読める。
 *
 * **型の札は名前の左に置く。** どのアウトプットの詳細かを見失わないためである（Dhik 2026-09-29
 * 「流石にないと迷子になる」）。
 *
 * **鎖の中の順は名前を指すと出す。** 業務マップの並びでも分かるので、毎回読む行にしない。
 * 説明は名前の横の ⓘ に入れる（説明が無ければ ⓘ を出さない）。
 *
 * **出口の帯は「出口の業務 → 結末 → 次のステージ」の 1 行で、右端に前のステージを置く。**
 * 図の終わりの札でも読めるので、「出口」を押して開く畳みに入れる（既定は閉じる）。入口は前の
 * ステージの出口と同じ結末の行なので、前のステージとして同じ畳みに出す。
 *
 * @param props ステージ・案件番号
 * @returns 見出しと畳んだ出口の帯
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ステージの見出し（型の札・名前・鎖の中の順は指すと出る・説明の ⓘ）と、「出口」を押して開く出口の業務 → 結末 → 次のステージ・前のステージの帯を描く。
 * @graph-connects NodeTypeBadge [calls] 見出しの左上の型の札
 * @graph-connects BusinessDetailLink [calls] 出口の業務の業務詳細へのリンク
 * @graph-connects StageLink [calls] 前後のステージの図へのリンク
 * @graph-connects FlowInfoMark [calls] 説明の ⓘ
 */
function StageHeading({ stage, pjNumber }: StageHeadingProps) {
  const { t } = useTranslation();
  const { chain, exit } = stage;
  const stageLinkOf = (reference: { id: number; label: string }) => (
    <StageLink key={reference.id} stageId={reference.id} pjNumber={pjNumber}>
      {reference.label === '' ? t('storyFlow.labelMissing') : reference.label}
    </StageLink>
  );
  const description =
    stage.description !== ''
      ? stage.description
      : stage.descriptionUntranslated
        ? t('spec.untranslated')
        : null;

  return (
    <header className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <NodeTypeBadge nodeType="Stage" />
        <h1
          className="text-2xl font-bold text-[var(--color-text-primary)]"
          data-stage-heading={stage.stageId}
          data-stage-position={chain.position ?? 'cross'}
          title={
            chain.position === null
              ? t('stage.crossStage')
              : t('stage.position', { position: chain.position })
          }
        >
          {stage.label === '' ? t('storyFlow.labelMissing') : stage.label}
        </h1>
        <FlowInfoMark lines={description === null ? [] : [description]} />
      </div>
      <details className={STAGE_BAND_CLASS} data-stage-exit={exit === null ? 'none' : 'set'}>
        <summary className="cursor-pointer font-semibold text-[var(--color-text-muted)]">
          {t('stage.exit')}
        </summary>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          {exit === null ? (
            <span className="text-[var(--color-text-secondary)]">{t('stage.noExit')}</span>
          ) : (
            /* 矢印は記号だけなので訳さない（3 言語で同じ綴りになる）。 */
            <span className="flex flex-wrap items-center gap-2 text-[var(--color-text-secondary)]">
              <span data-stage-exit-business>
                <BusinessDetailLink businessId={exit.business.id} pjNumber={pjNumber}>
                  {refLabelOf(exit.business, t)}
                </BusinessDetailLink>
              </span>
              <span aria-hidden="true">→</span>
              <span data-stage-exit-ending>
                {t('stage.exitEnding', { ending: refLabelOf(exit.ending, t) })}
              </span>
              <span aria-hidden="true">→</span>
              <span className="flex flex-wrap items-center gap-2" data-stage-next>
                <span>{t('stage.nextStage')}</span>
                {chain.next.length === 0 ? (
                  <span>{t('stage.noNextStage')}</span>
                ) : (
                  chain.next.map(stageLinkOf)
                )}
              </span>
            </span>
          )}
          <span className="ml-auto flex flex-wrap items-center gap-2" data-stage-prev>
            <span className="text-[var(--color-text-muted)]">{t('stage.prevStage')}</span>
            {chain.prev === null ? (
              <span className="text-[var(--color-text-muted)]">{prevAbsentText(stage, t)}</span>
            ) : (
              stageLinkOf(chain.prev)
            )}
          </span>
        </div>
      </details>
    </header>
  );
}

/**
 * 前のステージが無いときの断り。鎖に入らない（横断）・入口を持たない（最初）・入口の業務を出口に
 * 持つステージが無い（入口の参照が宙に浮いている）の 3 つを分ける。
 * @graph-connects none
 */
function prevAbsentText(stage: StageView, t: (key: string) => string): string {
  if (stage.chain.position === null) return t('stage.prevCross');
  return stage.entry === null ? t('stage.prevFirst') : t('stage.prevUnresolved');
}

/**
 * 出口の帯の見た目。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 出口の帯の見た目
 * @graph-connects none
 */
const STAGE_BAND_CLASS =
  'rounded border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-3 py-2 text-sm';

/** `SupportFlowCard` の props。 @graph-connects none */
interface SupportFlowCardProps {
  /** サブフローの札 */
  card: SupportFlowCardView;
  /** 重ねる案件番号 */
  pjNumber: number | undefined;
}

/**
 * サブフローの札 1 枚。名前だけを出し、札全体がその業務
 * フローの図へのリンクになる。状況や入口・出口はその業務フローの図が持つ。
 *
 * @param props 札と案件番号
 * @returns サブフローの札
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business サブフローを、名前だけを持つ、業務フローの図へのリンクの札として描く。
 * @graph-connects FlowLink [calls] 業務フローの図へのリンク
 */
function SupportFlowCard({ card, pjNumber }: SupportFlowCardProps) {
  const { t } = useTranslation();
  return (
    <FlowLink
      flowId={card.flowId}
      pjNumber={pjNumber}
      className="flex h-full flex-col gap-1.5 rounded border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-3 text-sm hover:border-[var(--color-accent-primary)]"
    >
      <span
        className="font-medium text-[var(--color-text-primary)]"
        data-support-flow={card.flowId}
      >
        {card.label === '' ? t('storyFlow.labelMissing') : card.label}
      </span>
    </FlowLink>
  );
}

/** `StageSkeleton` の props。 @graph-connects none */
interface StageSkeletonProps {
  /** 読み上げに出す取得中の名前 */
  label: string;
}

/**
 * 取得中の骨組み。
 *
 * @param props 読み上げに出す名前
 * @returns 取得中の骨組み
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ステージの図の取得中を骨組みで描く。
 * @graph-connects none
 */
function StageSkeleton({ label }: StageSkeletonProps) {
  return (
    <div className="flex flex-col gap-4" aria-busy="true" aria-label={label}>
      <Skeleton variant="rectangular" height="4rem" />
      <Skeleton variant="rectangular" height="24rem" />
    </div>
  );
}

/** `StageNotFound` の props。 @graph-connects none */
interface StageNotFoundProps {
  /** URL に書かれていた綴り */
  id: string;
}

/**
 * ステージが見つからないときの案内。ステージは業務マップから辿るので、戻り先は業務マップ。
 *
 * @param props URL に書かれていた綴り
 * @returns 未存在の案内
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ステージが見つからないことを、業務マップへの戻り口つきで案内する。
 * @graph-connects none
 */
function StageNotFound({ id }: StageNotFoundProps) {
  const { t } = useTranslation();
  return (
    <NotFoundState
      id={id}
      action={
        <Link
          to={BUSINESS_MAP_ROUTE}
          className="text-sm text-[var(--color-accent-primary)] underline"
        >
          {t('spec.backToList')}
        </Link>
      }
    />
  );
}
